import _slicedToArray from "/home/ubuntu/code/addictive-builder/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _toConsumableArray from "/home/ubuntu/code/addictive-builder/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _classCallCheck from "/home/ubuntu/code/addictive-builder/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/home/ubuntu/code/addictive-builder/node_modules/@babel/runtime/helpers/esm/createClass.js";
import { _optionalChain } from '@sentry/utils/esm/buildPolyfills';
import { logger, loadModule, fill, isThenable } from '@sentry/utils';
import { shouldDisableAutoInstrumentation } from './utils/node-utils.js'; // This allows us to use the same array for both defaults options and the type itself.
// (note `as const` at the end to make it a union of string literal types (i.e. "a" | "b" | ... )
// and not just a string[])

var OPERATIONS = ['aggregate', // aggregate(pipeline, options, callback)
'bulkWrite', // bulkWrite(operations, options, callback)
'countDocuments', // countDocuments(query, options, callback)
'createIndex', // createIndex(fieldOrSpec, options, callback)
'createIndexes', // createIndexes(indexSpecs, options, callback)
'deleteMany', // deleteMany(filter, options, callback)
'deleteOne', // deleteOne(filter, options, callback)
'distinct', // distinct(key, query, options, callback)
'drop', // drop(options, callback)
'dropIndex', // dropIndex(indexName, options, callback)
'dropIndexes', // dropIndexes(options, callback)
'estimatedDocumentCount', // estimatedDocumentCount(options, callback)
'find', // find(query, options, callback)
'findOne', // findOne(query, options, callback)
'findOneAndDelete', // findOneAndDelete(filter, options, callback)
'findOneAndReplace', // findOneAndReplace(filter, replacement, options, callback)
'findOneAndUpdate', // findOneAndUpdate(filter, update, options, callback)
'indexes', // indexes(options, callback)
'indexExists', // indexExists(indexes, options, callback)
'indexInformation', // indexInformation(options, callback)
'initializeOrderedBulkOp', // initializeOrderedBulkOp(options, callback)
'insertMany', // insertMany(docs, options, callback)
'insertOne', // insertOne(doc, options, callback)
'isCapped', // isCapped(options, callback)
'mapReduce', // mapReduce(map, reduce, options, callback)
'options', // options(options, callback)
'parallelCollectionScan', // parallelCollectionScan(options, callback)
'rename', // rename(newName, options, callback)
'replaceOne', // replaceOne(filter, doc, options, callback)
'stats', // stats(options, callback)
'updateMany', // updateMany(filter, update, options, callback)
'updateOne' // updateOne(filter, update, options, callback)
]; // All of the operations above take `options` and `callback` as their final parameters, but some of them
// take additional parameters as well. For those operations, this is a map of
// { <operation name>:  [<names of additional parameters>] }, as a way to know what to call the operation's
// positional arguments when we add them to the span's `data` object later

var OPERATION_SIGNATURES = {
  // aggregate intentionally not included because `pipeline` arguments are too complex to serialize well
  // see https://github.com/getsentry/sentry-javascript/pull/3102
  bulkWrite: ['operations'],
  countDocuments: ['query'],
  createIndex: ['fieldOrSpec'],
  createIndexes: ['indexSpecs'],
  deleteMany: ['filter'],
  deleteOne: ['filter'],
  distinct: ['key', 'query'],
  dropIndex: ['indexName'],
  find: ['query'],
  findOne: ['query'],
  findOneAndDelete: ['filter'],
  findOneAndReplace: ['filter', 'replacement'],
  findOneAndUpdate: ['filter', 'update'],
  indexExists: ['indexes'],
  insertMany: ['docs'],
  insertOne: ['doc'],
  mapReduce: ['map', 'reduce'],
  rename: ['newName'],
  replaceOne: ['filter', 'doc'],
  updateMany: ['filter', 'update'],
  updateOne: ['filter', 'update']
};

function isCursor(maybeCursor) {
  return maybeCursor && typeof maybeCursor === 'object' && maybeCursor.once && typeof maybeCursor.once === 'function';
}
/** Tracing integration for mongo package */


var Mongo = /*#__PURE__*/function () {
  /**
   * @inheritDoc
   */
  function Mongo() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    _classCallCheck(this, Mongo);

    ;

    Mongo.prototype.__init.call(this);

    this._operations = Array.isArray(options.operations) ? options.operations : OPERATIONS;
    this._describeOperations = 'describeOperations' in options ? options.describeOperations : true;
    this._useMongoose = !!options.useMongoose;
  }
  /**
   * @inheritDoc
   */


  _createClass(Mongo, [{
    key: "__init",
    value:
    /**
     * @inheritDoc
     */
    function __init() {
      this.name = Mongo.id;
    }
  }, {
    key: "setupOnce",
    value: function setupOnce(_, getCurrentHub) {
      if (shouldDisableAutoInstrumentation(getCurrentHub)) {
        (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log('Mongo Integration is skipped because of instrumenter configuration.');
        return;
      }

      var moduleName = this._useMongoose ? 'mongoose' : 'mongodb';
      var pkg = loadModule(moduleName);

      if (!pkg) {
        (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.error("Mongo Integration was unable to require `".concat(moduleName, "` package."));
        return;
      }

      this._instrumentOperations(pkg.Collection, this._operations, getCurrentHub);
    }
    /**
     * Patches original collection methods
     */

  }, {
    key: "_instrumentOperations",
    value: function _instrumentOperations(collection, operations, getCurrentHub) {
      var _this = this;

      operations.forEach(function (operation) {
        return _this._patchOperation(collection, operation, getCurrentHub);
      });
    }
    /**
     * Patches original collection to utilize our tracing functionality
     */

  }, {
    key: "_patchOperation",
    value: function _patchOperation(collection, operation, getCurrentHub) {
      if (!(operation in collection.prototype)) return;

      var getSpanContext = this._getSpanContextFromOperationArguments.bind(this);

      fill(collection.prototype, operation, function (orig) {
        return function () {
          var _this2 = this;

          for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
            args[_key] = arguments[_key];
          }

          var lastArg = args[args.length - 1];
          var scope = getCurrentHub().getScope();

          var parentSpan = _optionalChain([scope, 'optionalAccess', function (_2) {
            return _2.getSpan;
          }, 'call', function (_3) {
            return _3();
          }]); // Check if the operation was passed a callback. (mapReduce requires a different check, as
          // its (non-callback) arguments can also be functions.)


          if (typeof lastArg !== 'function' || operation === 'mapReduce' && args.length === 2) {
            var _span = _optionalChain([parentSpan, 'optionalAccess', function (_4) {
              return _4.startChild;
            }, 'call', function (_5) {
              return _5(getSpanContext(_this2, operation, args));
            }]);

            var maybePromiseOrCursor = orig.call.apply(orig, [this].concat(args));

            if (isThenable(maybePromiseOrCursor)) {
              return maybePromiseOrCursor.then(function (res) {
                _optionalChain([_span, 'optionalAccess', function (_6) {
                  return _6.finish;
                }, 'call', function (_7) {
                  return _7();
                }]);

                return res;
              });
            } // If the operation returns a Cursor
            // we need to attach a listener to it to finish the span when the cursor is closed.
            else if (isCursor(maybePromiseOrCursor)) {
              var cursor = maybePromiseOrCursor;

              try {
                cursor.once('close', function () {
                  _optionalChain([_span, 'optionalAccess', function (_8) {
                    return _8.finish;
                  }, 'call', function (_9) {
                    return _9();
                  }]);
                });
              } catch (e) {
                // If the cursor is already closed, `once` will throw an error. In that case, we can
                // finish the span immediately.
                _optionalChain([_span, 'optionalAccess', function (_10) {
                  return _10.finish;
                }, 'call', function (_11) {
                  return _11();
                }]);
              }

              return cursor;
            } else {
              _optionalChain([_span, 'optionalAccess', function (_12) {
                return _12.finish;
              }, 'call', function (_13) {
                return _13();
              }]);

              return maybePromiseOrCursor;
            }
          }

          var span = _optionalChain([parentSpan, 'optionalAccess', function (_14) {
            return _14.startChild;
          }, 'call', function (_15) {
            return _15(getSpanContext(_this2, operation, args.slice(0, -1)));
          }]);

          return orig.call.apply(orig, [this].concat(_toConsumableArray(args.slice(0, -1)), [function (err, result) {
            _optionalChain([span, 'optionalAccess', function (_16) {
              return _16.finish;
            }, 'call', function (_17) {
              return _17();
            }]);

            lastArg(err, result);
          }]));
        };
      });
    }
    /**
     * Form a SpanContext based on the user input to a given operation.
     */

  }, {
    key: "_getSpanContextFromOperationArguments",
    value: function _getSpanContextFromOperationArguments(collection, operation, args) {
      var data = {
        collectionName: collection.collectionName,
        dbName: collection.dbName,
        namespace: collection.namespace
      };
      var spanContext = {
        op: 'db',
        description: operation,
        data: data
      }; // If the operation takes no arguments besides `options` and `callback`, or if argument
      // collection is disabled for this operation, just return early.

      var signature = OPERATION_SIGNATURES[operation];
      var shouldDescribe = Array.isArray(this._describeOperations) ? this._describeOperations.includes(operation) : this._describeOperations;

      if (!signature || !shouldDescribe) {
        return spanContext;
      }

      try {
        // Special case for `mapReduce`, as the only one accepting functions as arguments.
        if (operation === 'mapReduce') {
          var _args = _slicedToArray(args, 2),
              map = _args[0],
              reduce = _args[1];

          data[signature[0]] = typeof map === 'string' ? map : map.name || '<anonymous>';
          data[signature[1]] = typeof reduce === 'string' ? reduce : reduce.name || '<anonymous>';
        } else {
          for (var i = 0; i < signature.length; i++) {
            data[signature[i]] = JSON.stringify(args[i]);
          }
        }
      } catch (_oO) {// no-empty
      }

      return spanContext;
    }
  }], [{
    key: "__initStatic",
    value:
    /**
     * @inheritDoc
     */
    function __initStatic() {
      this.id = 'Mongo';
    }
  }]);

  return Mongo;
}();

Mongo.__initStatic();

export { Mongo };